/** QuickRouter — Dessin du schéma en primitives, partagé par l'éditeur (React) et le rendu
 *  statique (image de l'assistant, export SVG/PDF).
 *
 * Primitive : { t: 'line'|'poly'|'circle'|'text', pts, c, r, fill, text, pos, size, anchor
 * (start|middle|end), rot (0|90), cls (body|pin|pinnum|pinname|ref|value|label|power|wire) }.
 * Repère du schéma : mm, Y vers le haut. Les textes restent lisibles (jamais à l'envers).
 */
import { symbolFor, placeSym, placedPins, placedBox, G } from './symbols.js';

const r3 = (v) => Math.round(v * 1000) / 1000;
export const textW = (s, size) => String(s).length * size * 0.72;
const DIRV = { R: [1, 0], L: [-1, 0], U: [0, 1], D: [0, -1] };

/** Arc en « bosses » d'inductance de a à b (n bosses), en points du repère du symbole. */
function humps(a, b, n) {
  const pts = [];
  const len = b[0] - a[0];
  const r = len / n / 2;
  for (let k = 0; k < n; k++) {
    const cx = a[0] + r * (2 * k + 1);
    for (let s = 0; s <= 8; s++) {
      const ang = Math.PI - (Math.PI * s) / 8;
      pts.push([cx + r * Math.cos(ang), a[1] + r * Math.sin(ang)]);
    }
  }
  return pts;
}

/** Primitives d'un symbole posé : corps, broches, noms, numéros, repère, valeur. */
export function symbolPrims(pl, sym, comp) {
  const out = [];
  const T = (p) => placeSym(pl, p);
  for (const sh of sym.shapes) {
    if (sh.kind === 'line') out.push({ t: 'line', pts: sh.pts.map(T), cls: 'body' });
    else if (sh.kind === 'rect') {
      const [[x0, y0], [x1, y1]] = sh.pts;
      out.push({ t: 'poly', pts: [[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]].map(T), fill: 'body', cls: 'body' });
    } else if (sh.kind === 'poly') out.push({ t: 'poly', pts: sh.pts.map(T), fill: sh.fill ? 'ink' : null, cls: 'body' });
    else if (sh.kind === 'circle') out.push({ t: 'circle', c: T(sh.c), r: sh.r, fill: null, cls: 'body' });
    else if (sh.kind === 'arcs') out.push({ t: 'line', pts: humps(sh.pts[0], sh.pts[1], sh.n).map(T), cls: 'body' });
  }
  for (const p of placedPins(pl, sym)) {
    const [dx, dy] = DIRV[p.dir];
    const edge = [r3(p.pos[0] - dx * p.len), r3(p.pos[1] - dy * p.len)];
    out.push({ t: 'line', pts: [p.pos, edge], cls: 'pin' });
    if (p.hideName) continue;
    const horiz = p.dir === 'L' || p.dir === 'R';
    const mid = [(p.pos[0] + edge[0]) / 2, (p.pos[1] + edge[1]) / 2];
    // Numéro de broche au-dessus (ou à gauche) de la broche, nom à l'intérieur du corps.
    out.push({ t: 'text', text: p.num, pos: horiz ? [mid[0], mid[1] + 0.75] : [mid[0] - 0.75, mid[1]], size: 1.0, anchor: 'middle', rot: horiz ? 0 : 90, cls: 'pinnum' });
    if (p.name && p.name !== p.num) {
      const inside = [edge[0] - dx * 0.7, edge[1] - dy * 0.7];
      const anchor = p.dir === 'L' || p.dir === 'D' ? 'start' : 'end';
      out.push({ t: 'text', text: p.name, pos: inside, size: 1.25, anchor, rot: horiz ? 0 : 90, cls: 'pinname' });
    }
  }
  // Repère et valeur : à droite du symbole s'il est vertical, sinon aux places prévues.
  const b = placedBox(pl, sym);
  const vertical = pl.rot === 90 || pl.rot === 270;
  const value = comp.value || '';
  if (vertical) {
    out.push({ t: 'text', text: comp.ref, pos: [b[2] + 0.8, (b[1] + b[3]) / 2 + (value ? 1.0 : 0)], size: 1.6, anchor: 'start', rot: 0, cls: 'ref' });
    if (value) out.push({ t: 'text', text: value, pos: [b[2] + 0.8, (b[1] + b[3]) / 2 - 1.1], size: 1.4, anchor: 'start', rot: 0, cls: 'value' });
  } else if (sym.anchor === 'start') {
    // MOSFET : textes à côté du corps, du côté opposé à la grille.
    const right = !pl.mirror === (pl.rot === 0);
    const x = right ? b[2] + 0.6 : b[0] - 0.6;
    const anchor = right ? 'start' : 'end';
    out.push({ t: 'text', text: comp.ref, pos: [x, (b[1] + b[3]) / 2 + 1.0], size: 1.6, anchor, rot: 0, cls: 'ref' });
    if (value) out.push({ t: 'text', text: value, pos: [x, (b[1] + b[3]) / 2 - 1.1], size: 1.4, anchor, rot: 0, cls: 'value' });
  } else {
    const cx = (b[0] + b[2]) / 2;
    const tight = sym.kindName && ['R', 'C', 'L', 'F', 'D', 'SW'].includes(sym.kindName);
    out.push({ t: 'text', text: comp.ref, pos: [cx, b[3] + (tight ? -0.3 : 1.3)], size: 1.6, anchor: 'middle', rot: 0, cls: 'ref' });
    if (value) out.push({ t: 'text', text: value, pos: [cx, b[1] - (tight ? -0.3 : 1.3)], size: 1.4, anchor: 'middle', rot: 0, cls: 'value' });
  }
  return out;
}

/** Repère « dans le sens de dir » : x vers dir, y à gauche de dir. */
function frame(origin, dir) {
  const [dx, dy] = DIRV[dir];
  return (x, y) => [r3(origin[0] + x * dx - y * dy), r3(origin[1] + x * dy + y * dx)];
}

/**
 * Primitives d'une étiquette, ancrée en `at` (bout de broche ou point de fil), sortant vers dir.
 * net : drapeau pentagonal ; power : barre (+ nom) vers le haut ; gnd : symbole de masse vers le bas.
 */
export function labelPrims(label, at, dir) {
  const out = [];
  const size = 1.3;
  if (label.kind === 'net') {
    const w = textW(label.name, size) + 0.9, h = size * 1.6;
    if (!dir) { // étiquette posée sur un fil : texte au-dessus du fil, petit repère
      out.push({ t: 'line', pts: [[at[0], at[1]], [at[0], at[1] + 0.6]], cls: 'label' });
      out.push({ t: 'text', text: label.name, pos: [at[0] + 0.3, at[1] + 1.4], size, anchor: 'start', rot: 0, cls: 'label' });
      return out;
    }
    const F = frame(at, dir);
    out.push({ t: 'poly', pts: [F(0, 0), F(h / 2, h / 2), F(h / 2 + w, h / 2), F(h / 2 + w, -h / 2), F(h / 2, -h / 2), F(0, 0)], fill: 'label', cls: 'label' });
    const horiz = dir === 'L' || dir === 'R';
    const tp = F(h / 2 + 0.45, 0);
    const anchor = dir === 'R' || dir === 'U' ? 'start' : 'end';
    out.push({ t: 'text', text: label.name, pos: tp, size, anchor, rot: horiz ? 0 : 90, cls: 'label' });
    return out;
  }
  // Alimentation / masse : court fil dans le sens de la broche, puis symbole vertical.
  let base = at;
  if (dir) {
    const [dx, dy] = DIRV[dir];
    base = [r3(at[0] + dx * G), r3(at[1] + dy * G)];
    out.push({ t: 'line', pts: [at, base], cls: 'wire' });
  }
  if (label.kind === 'gnd') {
    const down = dir === 'U' ? 1 : -1;
    const y0 = base[1] + down * 1.27;
    out.push({ t: 'line', pts: [base, [base[0], y0]], cls: 'power' });
    [[1.5, 0], [1.0, 0.5], [0.45, 1.0]].forEach(([hw, dy]) => out.push({ t: 'line', pts: [[base[0] - hw, y0 + down * dy], [base[0] + hw, y0 + down * dy]], cls: 'power' }));
    if (!/^gnd$/i.test(label.name)) out.push({ t: 'text', text: label.name, pos: [base[0], y0 + down * 2.4], size: 1.1, anchor: 'middle', rot: 0, cls: 'power' });
  } else {
    const up = dir === 'D' ? -1 : 1;
    const y0 = base[1] + up * 1.27;
    out.push({ t: 'line', pts: [base, [base[0], y0]], cls: 'power' });
    out.push({ t: 'line', pts: [[base[0] - 1.27, y0], [base[0] + 1.27, y0]], cls: 'power' });
    out.push({ t: 'text', text: label.name, pos: [base[0], y0 + up * 1.5], size: 1.3, anchor: 'middle', rot: 0, cls: 'power' });
  }
  return out;
}

/** Ancrage et direction de chaque étiquette : [{ label, at, dir }] (ignore les orphelines). */
export function labelAnchors(project, pinsByComp) {
  const out = [];
  for (const l of project.schematic?.labels ?? []) {
    if (l.at) {
      const p = pinsByComp.get(l.at.comp)?.find((q) => q.num === l.at.pin);
      if (p) out.push({ label: l, at: p.pos, dir: p.dir });
    } else if (l.pos) out.push({ label: l, at: l.pos, dir: null });
  }
  return out;
}

/** Points de jonction : là où se rejoignent au moins trois branches. */
export function junctions(project, pins) {
  const wires = project.schematic?.wires ?? [];
  const key = (p) => `${Math.round(p[0] * 100)},${Math.round(p[1] * 100)}`;
  const count = new Map(), at = new Map();
  const bump = (p, n) => { const k = key(p); count.set(k, (count.get(k) ?? 0) + n); at.set(k, p); };
  for (const w of wires) {
    w.pts.forEach((p, i) => bump(p, i === 0 || i === w.pts.length - 1 ? 1 : 2));
  }
  for (const p of pins) if (count.has(key(p.pos))) bump(p.pos, 1);
  // Extrémité posée au milieu d'un segment d'un autre fil : deux branches de plus.
  for (const w of wires) {
    for (const e of [w.pts[0], w.pts[w.pts.length - 1]]) {
      for (const o of wires) {
        if (o === w) continue;
        for (let i = 1; i < o.pts.length; i++) {
          const a = o.pts[i - 1], b = o.pts[i];
          const inX = Math.min(a[0], b[0]) - 0.01 < e[0] && e[0] < Math.max(a[0], b[0]) + 0.01;
          const inY = Math.min(a[1], b[1]) - 0.01 < e[1] && e[1] < Math.max(a[1], b[1]) + 0.01;
          const colinear = Math.abs((b[0] - a[0]) * (e[1] - a[1]) - (b[1] - a[1]) * (e[0] - a[0])) < 0.01;
          const isEnd = key(a) === key(e) || key(b) === key(e);
          if (inX && inY && colinear && !isEnd) bump(e, 2);
        }
      }
    }
  }
  return [...count.entries()].filter(([, n]) => n >= 3).map(([k]) => at.get(k));
}

/** Tout le dessin du schéma : symboles (par composant), fils, jonctions, étiquettes, boîte. */
export function schematicDrawing(project, lookup) {
  const sch = project.schematic ?? { symbols: [], wires: [], labels: [], notes: [] };
  const byId = new Map(project.components.map((c) => [c.id, c]));
  const symbols = [];
  const pins = [];
  const pinsByComp = new Map();
  for (const pl of sch.symbols) {
    const comp = byId.get(pl.comp);
    const sym = comp && symbolFor(lookup(comp.footprint));
    if (!sym) continue;
    const pp = placedPins(pl, sym).map((p) => ({ ...p, comp: comp.id, ref: comp.ref, key: `${comp.ref}.${p.num}` }));
    pins.push(...pp);
    pinsByComp.set(comp.id, pp);
    symbols.push({ comp, pl, sym, box: placedBox(pl, sym), prims: symbolPrims(pl, sym, comp), pins: pp });
  }
  const labels = labelAnchors(project, pinsByComp).map((a) => ({ ...a, prims: labelPrims(a.label, a.at, a.dir) }));
  const dots = junctions(project, pins);
  // Boîte de tout le dessin (cadrage).
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const grow = (p) => { x0 = Math.min(x0, p[0]); y0 = Math.min(y0, p[1]); x1 = Math.max(x1, p[0]); y1 = Math.max(y1, p[1]); };
  for (const s of symbols) { grow([s.box[0] - 4, s.box[1] - 4]); grow([s.box[2] + 8, s.box[3] + 4]); }
  for (const w of sch.wires) w.pts.forEach(grow);
  for (const l of labels) l.prims.forEach((p) => (p.pts ?? (p.pos ? [p.pos] : [])).forEach(grow));
  for (const n of sch.notes ?? []) grow(n.pos);
  const box = Number.isFinite(x0) ? [x0, y0, x1, y1] : [0, 0, 297, 210];
  return { symbols, pins, labels, dots, wires: sch.wires, notes: sch.notes ?? [], box };
}
